import React, { useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { InputText } from 'primereact/inputtext';
import { unMask, mask as applyMask } from 'remask';

/**
 * Componente de campo de data mascarado com auto-focus
 * @param {Object} props - Propriedades do componente
 * @param {string} props.value - Valor atual do campo
 * @param {function} props.onChange - Função chamada quando o valor muda
 * @param {React.Ref} props.nextFieldRef - Referência para o próximo campo que receberá focus
 * @param {string} props.placeholder - Placeholder do campo
 * @param {string} props.className - Classes CSS
 * @param {boolean} props.disabled - Se o campo está desabilitado
 * @param {Object} ref - Referência do componente
 */
export const DateMaskedField = forwardRef(({
  value = '',
  onChange,
  nextFieldRef,
  placeholder = 'dd/mm/aaaa',
  className = 'w-full',
  disabled = false,
  ...rest
}, ref) => {
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    blur: () => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    },
    getValue: () => value
  }));

  const focusNextField = useCallback(() => {
    if (nextFieldRef && nextFieldRef.current) {
      setTimeout(() => {
        nextFieldRef.current.focus();
      }, 50);
    }
  }, [nextFieldRef]);

  const isValidDate = (dateString) => {
    if (!dateString || dateString.length !== 10) return false;
    
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateString.match(regex);
    
    if (!match) return false;
    
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1900 || year > 2100) return false;
    
    const date = new Date(year, month - 1, day);
    
    return date.getFullYear() === year &&
           date.getMonth() === month - 1 &&
           date.getDate() === day;
  };

  const parseDate = (dateString) => {
    if (!isValidDate(dateString)) return null;
    
    const [day, month, year] = dateString.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };

  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date)) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    const isDeleting = e.nativeEvent.inputType === 'deleteContentBackward' ||
                      e.nativeEvent.inputType === 'deleteContentForward';
    
    if (!isDeleting && newValue) {
      const unMasked = unMask(newValue);
      
      if (unMasked.length > 0) {
        const masked = applyMask(unMasked, '99/99/9999');
        newValue = masked;
        e.target.value = masked;
      }
    }
    
    if (onChange) {
      if (newValue.length === 10 && isValidDate(newValue)) {
        const dateObj = parseDate(newValue);
        onChange(dateObj);
        
        focusNextField();
      } else {
        onChange(newValue);
      }
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === 'Tab') && value && getDisplayValue().length === 10) {
      e.preventDefault();
      focusNextField();
    }
  };

  const getDisplayValue = () => {
    if (!value) return '';
    
    if (value instanceof Date) {
      return formatDate(value);
    }
    
    return value.toString();
  };

  return (
    <InputText
      ref={inputRef}
      value={getDisplayValue()}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      {...rest}
    />
  );
});

DateMaskedField.displayName = 'DateMaskedField';

export default DateMaskedField;